import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { contactInfo, services } from '../mockData';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    service: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.service) {
      toast.error('Lütfen tüm zorunlu alanları doldurun');
      return;
    }

    // Mock submission
    console.log('Form submitted:', formData);
    toast.success('Mesajınız alındı! En kısa sürede sizinle iletişime geçeceğiz.');
    
    // Reset form
    setFormData({
      name: '',
      phone: '',
      service: '',
      message: ''
    });
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="min-h-screen bg-black pt-24">
      <section className="section-padding">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-gradient-gold">İletişim</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Bizimle iletişime geçin, size en iyi hizmeti sunalım
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">İletişim Bilgileri</h2>
                <div className="space-y-6">
                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center border border-yellow-500/30 flex-shrink-0">
                      <MapPin className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Adres</h3>
                      <p className="text-gray-400">{contactInfo.address}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center border border-yellow-500/30 flex-shrink-0">
                      <Phone className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-2">Telefon</h3>
                      <div className="space-y-1">
                        {contactInfo.phones.map((phone, index) => (
                          <a 
                            key={index}
                            href={`tel:${phone.number.replace(/\s/g, '')}`}
                            className="block text-gray-400 hover:text-yellow-500"
                          >
                            {phone.number} ({phone.label})
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center border border-yellow-500/30 flex-shrink-0">
                      <Mail className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">E-posta</h3>
                      <a 
                        href={`mailto:${contactInfo.email}`}
                        className="text-gray-400 hover:text-yellow-500"
                      >
                        {contactInfo.email}
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center border border-yellow-500/30 flex-shrink-0">
                      <Instagram className="w-6 h-6 text-yellow-500" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-white mb-1">Instagram</h3>
                      <a 
                        href={contactInfo.instagram}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-gray-400 hover:text-yellow-500"
                      >
                        @emree_ticaret
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="rounded-xl overflow-hidden border border-zinc-800 h-64">
                <iframe
                  src={`https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3124.2!2d${contactInfo.mapCoordinates.lng}!3d${contactInfo.mapCoordinates.lat}!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzjCsDI3JzQ3LjIiTiAyN8KwMDknNTcuMiJF!5e0!3m2!1str!2str!4v1234567890`}
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="EMRE TİCARET Konum"
                ></iframe>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800">
              <h2 className="text-2xl font-bold text-white mb-6">Teklif İsteyin</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-white">Ad Soyad *</Label>
                  <Input
                    id="name"
                    placeholder="Adınız ve soyadınız"
                    value={formData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-white">Telefon *</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="0530 XXX XX XX"
                    value={formData.phone}
                    onChange={(e) => handleChange('phone', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="service" className="text-white">Hizmet Türü *</Label>
                  <Select value={formData.service} onValueChange={(value) => handleChange('service', value)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white">
                      <SelectValue placeholder="Hizmet seçin" />
                    </SelectTrigger>
                    <SelectContent className="bg-zinc-800 border-zinc-700">
                      {services.map((service) => (
                        <SelectItem key={service.id} value={service.id} className="text-white">
                          {service.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="text-white">Mesajınız</Label>
                  <Textarea
                    id="message"
                    placeholder="Detayları buraya yazabilirsiniz..."
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)}
                    className="bg-zinc-800 border-zinc-700 text-white min-h-32"
                  />
                </div>

                <Button type="submit" className="w-full bg-yellow-500 text-black hover:bg-yellow-600">
                  <Send className="w-4 h-4 mr-2" />
                  Gönder
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
